import React, { useEffect, useState } from 'react';
import './ImageModal.css';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';

interface BandMember {
  firstName: string;
  lastName: string;
  phone: string;
}

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  bandName: string;
  bandId: number;
  imageUrl: string | null | undefined;
  isLoading: boolean;
  onImageUpload?: (file: File) => Promise<void>;
}

const ImageModal = ({ isOpen, onClose, bandName, bandId, imageUrl, isLoading, onImageUpload }: ImageModalProps) => {
  const [members, setMembers] = useState<BandMember[]>([]);
  const [isReady, setIsReady] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const [uploadError, setUploadError] = useState<string | null>(null);

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{3})$/);
    
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]}`;
    }
    
    return phone;
  };

  useEffect(() => {
    if (isOpen && bandId) {
      setIsReady(false);
      axiosPrivate.get(`/bandMembers/${bandId}/phones`)
        .then(response => {
          setMembers(response.data);
        })
        .catch(error => {
          console.error('Błąd podczas pobierania członków zespołu:', error);
          setMembers([]);
        })
        .finally(() => {
          setIsReady(true);
        });
    } else {
      setMembers([]);
      setIsReady(false);
    }
  }, [isOpen, bandId, axiosPrivate]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onImageUpload) {
      setUploadError(null);
      try {
        await onImageUpload(file);
      } catch (error) {
        setUploadError('Nie udało się przesłać zdjęcia. Spróbuj ponownie.');
      }
    }
  };

  if (!isOpen || !isReady) return null;

  return (
    <div className="image-modal-overlay">
      <div className="image-modal-content">
        <button className="image-modal-close" onClick={onClose}>✖</button>
        <h3>{bandName}</h3>
        {members.length > 0 && (
          <div className="band-members">
            {members.map((member, index) => (
              <div key={index} className="band-member">
                {member.firstName} {member.lastName} - {' '}
                <a 
                  href={`tel:${member.phone}`}
                  className="phone-link"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {formatPhoneNumber(member.phone)}
                </a>
              </div>
            ))}
          </div>
        )}
        {!isLoading && (
          imageUrl ? (
            <img src={imageUrl} alt={`Zdjęcie zespołu ${bandName}`} />
          ) : (
            <div className="upload-section">
              <input
                type="file"
                id="imageUpload"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <label htmlFor="imageUpload" className="upload-button">
                Wstaw zdjęcie
              </label>
              {uploadError && <p className="upload-error">{uploadError}</p>}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ImageModal;
